// src/services/crm.js
import axios from "../utils/axios";
import mailboxAxios from "../utils/mailboxAxios";
import { authHeaders } from "../utils/auth";

// --- Helperi ---
function read(key, seed) {
  const raw = localStorage.getItem(key);
  if (!raw) {
    localStorage.setItem(key, JSON.stringify(seed));
    return seed;
  }
  try { return JSON.parse(raw); } catch { return seed; }
}
function write(key, list) {
  localStorage.setItem(key, JSON.stringify(list));
}
function nextId(list) {
  return Math.max(0, ...list.map(x => x.id || 0)) + 1;
}

function mapLeadFromApi(db) {
  return {
    id: db.Id,
    partyName: db.Name,           // UI očekuje partyName
    email: db.Email,
    phone: db.Phone,
    source: db.SourceCode,
    stage: db.Status,             // UI "stage" == DB "Status"
    message: db.Message,
    notes: db.Notes,
    assignedTo: db.AssignedTo,
    createdAt: db.CreatedAtUtc,
  };
}


// --- Activities (API) ---

export async function fetchActivitiesForParty(partyId, { limit = 50, offset = 0 } = {}) {
  const url = new URL(`https://modularobjects.com/marketing/api/crm/parties/${partyId}/activities`);
  url.searchParams.set("limit", String(limit));
  url.searchParams.set("offset", String(offset));

  const r = await fetch(url, { credentials: "include" });
  const j = await r.json().catch(async () => ({ success:false, message: await r.text() }));

  if (!r.ok || !j?.success) throw new Error(j?.message || `HTTP ${r.status}`);

  return {
    items: j.items || [],
    total: j.total || 0,
    hasMore: !!j.hasMore,
    limit: j.limit || limit,
    offset: j.offset || offset,
  };
}

export async function createActivityForParty(partyId, body) {
  const r = await fetch(`https://modularobjects.com/marketing/api/crm/parties/${partyId}/activities`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(body),
  });
  const j = await r.json().catch(async ()=>({success:false, message: await r.text()}));
  if (!r.ok || !j?.success) throw new Error(j?.message || `HTTP ${r.status}`);
  return j.item;
}

export async function fetchRecentActivities(limit = 10) {
  const url = new URL(`https://modularobjects.com/marketing/api/crm/activities/recent`);
  url.searchParams.set("limit", String(limit));
  const res = await fetch(url, { credentials: "include" });
  const json = await res.json().catch(async () => ({ success:false, message: await res.text() }));
  if (!res.ok || !json?.success) throw new Error(json?.message || `HTTP ${res.status}`);
  return json.items || [];
}


// (opciono)
export async function updateActivity(id, patch) {
  const res = await fetch(`https://modularobjects.com/marketing/api/crm/activities/${id}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(patch),
  });
  const json = await res.json().catch(async () => ({ success:false, message: await res.text() }));
  if (!res.ok || !json?.success) throw new Error(json?.message || `HTTP ${res.status}`);
  return true;
}


// --- Leads ---
// services/crm.js
export async function fetchLeads({ q = "", stage = "", limit = 50, offset = 0 } = {}) {
  const u = new URL("https://modularobjects.com/marketing/api/form/leadform/list");
  if (q) u.searchParams.set("q", q);
  if (stage) u.searchParams.set("stage", stage);       // new|contacted|qualified|won|lost
  u.searchParams.set("limit", String(limit));
  u.searchParams.set("offset", String(offset));

  const res = await fetch(u, { credentials: "include" });

  let json = null;
  try {
    json = await res.json();
  } catch {
    const txt = await res.text().catch(() => "");
    throw new Error(txt || `HTTP ${res.status}`);
  }

  if (!res.ok || !json?.success) {
    throw new Error(json?.message || `HTTP ${res.status}`);
  }

  return json.items ?? [];
}


export async function createLead(form) {
  // mapiranje UI -> DB
  const payload = {
    Name: form.partyName || form.name,   // UI "partyName" -> DB "Name"
    Email: form.email || null,
    Phone: form.phone || null,
    Message: form.message ?? form.notes ?? null,
    SourceCode: form.source || null,
    Status: form.stage || 'new',
    AssignedTo: form.assignedTo || null,
    Notes: form.notes || null,
  };
  const res = await fetch(`${BASE}/leads`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(payload),
  });
  const json = await res.json();
  if (!res.ok || !json.success) throw new Error(json.message || `HTTP ${res.status}`);
  return json.item;
}



export async function updateLead(id, patch) {
  const res = await fetch(`https://modularobjects.com/marketing/api/form/leadform/${id}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(patch),
    credentials: "include",
  });
  const json = await res.json().catch(async () => ({ success: false, message: await res.text() }));
  if (!res.ok || !json.success) throw new Error(json.message || `HTTP ${res.status}`);
  return true;
}


// services/crm.js
export async function convertLead(id, payload) {
  const res = await fetch(`https://modularobjects.com/marketing/api/form/leadform/${id}/convert`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(payload),
  });

  let json;
  try { json = await res.json(); }
  catch {
    const txt = await res.text().catch(() => "");
    throw new Error(txt || `HTTP ${res.status}`);
  }

  if (!res.ok || !json?.success) {
    throw new Error(json?.message || `HTTP ${res.status}`);
  }
  return json; // { opportunityId }
}


// --- Opportunities ---
export const OPPORTUNITY_STAGES = [
  "qualification", "proposal", "negotiation", "won", "lost"
];

export async function fetchOpportunities() {
  const res = await fetch("https://modularobjects.com/marketing/api/form/opportunities", {
    credentials: "include"
  });
  const json = await res.json();
  if (!res.ok || !json.success) throw new Error(json.message || `HTTP ${res.status}`);
  return json.items;
}

// helper za robustno parsiranje bez duplog čitanja
async function readJsonOrText(res) {
  // ako je JSON, probaj direktno
  const ct = res.headers.get("content-type") || "";
  if (ct.includes("application/json")) {
    try { return await res.json(); } catch { /* padni na klon */ }
  }
  // koristi klon da možeš da čitaš ponovo
  const clone = res.clone();
  try { return JSON.parse(await clone.text()); }
  catch { return { raw: await clone.text() }; }
}

export async function createOpportunity(payload) {
  const res = await fetch("https://modularobjects.com/marketing/api/form/opportunities", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({
      name: payload.name,
      account: payload.account,
      partyId: payload.partyId || null,
      amount: Number(payload.amount ?? 0),
      currency: payload.currency || "EUR",
      stage: payload.stage || "qualification",
      closeDate: payload.closeDate || "",
      notes: payload.notes || ""
    })
  });

  const data = await readJsonOrText(res);

  if (!res.ok || data?.success === false) {
    const msg = data?.message || data?.raw || `HTTP ${res.status}`;
    throw new Error(msg);
  }
  return data.item ?? data;
}


export async function updateOpportunity(id, patch) {
  const res = await fetch(`https://modularobjects.com/marketing/api/form/opportunities/${id}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(patch),
  });

  let json;
  try { json = await res.json(); }
  catch {
    const txt = await res.text().catch(() => "");
    throw new Error(txt || `HTTP ${res.status}`);
  }

  if (!res.ok || !json?.success) {
    throw new Error(json?.message || `HTTP ${res.status}`);
  }
  return true;
}

export async function fetchOpportunity(id) {
  const res = await fetch(`https://modularobjects.com/marketing/api/form/opportunities/${id}`, {
    credentials: "include",
  });
  let json;
  try { json = await res.json(); }
  catch {
    const txt = await res.text().catch(() => "");
    throw new Error(txt || `HTTP ${res.status}`);
  }
  if (!res.ok || !json?.success) throw new Error(json?.message || `HTTP ${res.status}`);
  return json.item;
}

export async function deleteOpportunity(id){
  const r = await fetch(`https://modularobjects.com/marketing/api/form/opportunities/${id}/delete`, {
    method:"PATCH", credentials:"include"
  });
  const j = await r.json(); if(!r.ok||!j.success) throw new Error(j.message||`HTTP ${r.status}`);
  return true;
}

export const fetchOpportunityNotes = (id)=> fetchJSON(`https://modularobjects.com/marketing/api/form/opportunities/${id}/notes`);
export const addOpportunityNote = (id, text)=> postJSON(`https://modularobjects.com/marketing/api/form/opportunities/${id}/notes`, { text });

async function fetchJSON(url){
  const r = await fetch(`https://modularobjects.com${url}`, { credentials:"include" });
  const j = await r.json(); if(!r.ok||!j.success) throw new Error(j.message||`HTTP ${r.status}`); return j.items||j.item||j;
}
async function postJSON(url, body){
  const r = await fetch(`https://modularobjects.com${url}`, { method:"POST", headers:{ "Content-Type":"application/json" }, credentials:"include", body: JSON.stringify(body) });
  const j = await r.json(); if(!r.ok||!j.success) throw new Error(j.message||`HTTP ${r.status}`); return j;
}

export async function fetchLeadFiles(leadId) {
  const res = await fetch(`https://modularobjects.com/marketing/api/form/leadform/${leadId}/files`, {
    credentials: "include",
  });
  let json;
  try { json = await res.json(); }
  catch { throw new Error(`HTTP ${res.status}`); }
  if (!res.ok || !json?.success) throw new Error(json?.message || `HTTP ${res.status}`);
  return json.items || [];
}


export async function deleteLeadFile(leadId, fileId) {
  const r = await fetch(`https://modularobjects.com/marketing/api/form/leadform/${leadId}/files/${fileId}`, {
    method: "DELETE",
    credentials: "include",
  });
  const j = await r.json().catch(()=>null);
  if (!r.ok || !j?.success) throw new Error(j?.message || `HTTP ${r.status}`);
  return true;
}

export async function uploadLeadFiles(leadId, files) {
  const fd = new FormData();
  files.forEach(f => fd.append("files", f));
  const r = await fetch(`https://modularobjects.com/marketing/api/form/leadform/${leadId}/files`, {
    method: "POST",
    body: fd,
    credentials: "include",
  });
  const j = await r.json().catch(()=>null);
  if (!r.ok || !j?.success) throw new Error(j?.message || `HTTP ${r.status}`);
  return j;
}

export async function fetchLeadNotes(leadId) {
  const res = await fetch(`https://modularobjects.com/marketing/api/form/leadform/${leadId}/notes`, {
    credentials: "include",
  });
  const json = await res.json().catch(async () => ({ success:false, message: await res.text() }));
  if (!res.ok || !json?.success) throw new Error(json?.message || `HTTP ${res.status}`);
  return json.items || [];
}

export async function addLeadNote(leadId, text) {
  const res = await fetch(`https://modularobjects.com/marketing/api/form/leadform/${leadId}/notes`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ text }),
  });
  const json = await res.json().catch(async () => ({ success:false, message: await res.text() }));
  if (!res.ok || !json?.success) throw new Error(json?.message || `HTTP ${res.status}`);
  return true;
}

const BASE = "https://modularobjects.com/marketing/api/crm";

export async function fetchParties({ q, role, limit=50, offset=0 } = {}) {
  const url = new URL("https://modularobjects.com/marketing/api/crm/parties");
  if (q) url.searchParams.set("q", q);
  if (role) url.searchParams.set("role", role);
  url.searchParams.set("limit", String(limit));
  url.searchParams.set("offset", String(offset));
  const res = await fetch(url, { credentials: "include" });
  const json = await res.json();
  if (!res.ok || !json.success) throw new Error(json.message || `HTTP ${res.status}`);
  return json.items ?? [];
}

// services/crm.js
export async function searchAll(q, limit=30, offset=0) {
  try {
    const url = new URL(`${BASE}/search`);
    url.searchParams.set('q', q);
    url.searchParams.set('limit', limit);
    url.searchParams.set('offset', offset);
    const res = await fetch(url, { credentials: 'include' });
    const json = await res.json();
    if (!res.ok || !json.success) throw new Error(json.message || `HTTP ${res.status}`);
    return json.items ?? [];
  } catch (e) {
    console.error('searchAll failed', e);
    return []; // da se ne prikazuje error toast dok rešavaš backend
  }
}




export async function createParty(body) {
  const res = await fetch(`${BASE}/parties`, {
    method: "POST", headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body), credentials: "include"
  });
  const json = await res.json();
  if (!res.ok || !json.success) throw new Error(json.message || `HTTP ${res.status}`);
  return json.item;
}

export async function updateParty(id, body) {
  const res = await fetch(`${BASE}/parties/${id}`, {
    method: "PATCH", headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body), credentials: "include"
  });
  const json = await res.json();
  if (!res.ok || !json.success) throw new Error(json.message || `HTTP ${res.status}`);
  return true;
}

export async function fetchContactsForParty(partyId) {
  const res = await fetch(`${BASE}/parties/${partyId}/contacts`, { credentials: "include" });
  const json = await res.json();
  if (!res.ok || !json.success) throw new Error(json.message || `HTTP ${res.status}`);
  return json.items;
}

export async function createContact(body) {
  const res = await fetch(`${BASE}/contacts`, {
    method: "POST", headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body), credentials: "include"
  });
  const json = await res.json();
  if (!res.ok || !json.success) throw new Error(json.message || `HTTP ${res.status}`);
  return json.item;
}

export async function updateContact(id, body) {
  const res = await fetch(`${BASE}/contacts/${id}`, {
    method: "PATCH", headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body), credentials: "include"
  });
  const json = await res.json();
  if (!res.ok || !json.success) throw new Error(json.message || `HTTP ${res.status}`);
  return true;
}
// ---------- helpers ----------
const CRM_BASE = "https://modularobjects.com/marketing/api/crm";

async function http(method, path, body) {
  const res = await fetch(`${CRM_BASE}${path}`, {
    method,
    credentials: "include",
    headers: body ? { "Content-Type": "application/json" } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  // pokušaj JSON → fallback na text
  let json;
  try { json = await res.json(); }
  catch { json = { success:false, message: await res.text() }; }
  if (!res.ok || json?.success === false) {
    throw new Error(json?.message || `HTTP ${res.status}`);
  }
  return json;
}

// ---------- Addresses ----------
export async function fetchAddressesForParty(partyId) {
  const j = await http("GET", `/party/${partyId}/addresses`);
  return j.items || [];
}
export async function createAddress(partyId, payload) {
  const j = await http("POST", `/party/${partyId}/addresses`, payload);
  return j.item;
}
export async function updateAddress(addressId, payload) {
  await http("PATCH", `/party/addresses/${addressId}`, payload);
  return true;
}


export async function deleteAddress(addressId) {
  const r = await fetch(`${CRM_BASE}/party/addresses/${addressId}`, {
    method: "DELETE",
    credentials: "include"
  });
  const j = await r.json().catch(()=>null);
  if (!r.ok || !j?.success) throw new Error(j?.message || `HTTP ${r.status}`);
  return true;
}

// ---------- Channels ----------
export async function fetchChannelsForParty(partyId) {
  const j = await http("GET", `/party/${partyId}/channels`);
  return j.items || [];
}
export async function createChannel(partyId, payload) {
  const j = await http("POST", `/party/${partyId}/channels`, payload);
  return j.item;
}
export async function updateChannel(channelId, payload) {
  await http("PATCH", `/party/channels/${channelId}`, payload);
  return true;
}
export async function deleteChannel(channelId) {
  await http("DELETE", `/party/channels/${channelId}`);
  return true;
}

// ---------- Bank Accounts ----------
export async function fetchBankAccountsForParty(partyId) {
  const j = await http("GET", `/party/${partyId}/bank-accounts`);
  return j.items || [];
}
export async function createBankAccount(partyId, payload) {
  const j = await http("POST", `/party/${partyId}/bank-accounts`, payload);
  return j.item;
}
export async function updateBankAccount(id, payload) {
  await http("PATCH", `/party/bank-accounts/${id}`, payload);
  return true;
}
export async function deleteBankAccount(id) {
  await http("DELETE", `/party/bank-accounts/${id}`);
  return true;
}

// ---------- Identifiers ----------
export async function fetchIdentifiersForParty(partyId) {
  const j = await http("GET", `/party/${partyId}/identifiers`);
  return j.items || [];
}
export async function createIdentifier(partyId, payload) {
  const j = await http("POST", `/party/${partyId}/identifiers`, payload);
  return j.item;
}
export async function updateIdentifier(id, payload) {
  await http("PATCH", `/party/identifiers/${id}`, payload);
  return true;
}
export async function deleteIdentifier(id) {
  await http("DELETE", `/party/identifiers/${id}`);
  return true;
}

export async function fetchCampaigns({ q="", status="", limit=50, offset=0 } = {}) {
  const params = new URLSearchParams();
  if (q) params.set("q", q);
  if (status) params.set("status", status);
  params.set("limit", String(limit));
  params.set("offset", String(offset));

  const r = await fetch(`https://modularobjects.com/marketing/api/admin/campaigns?${params.toString()}`, { credentials: "include" });
  if (!r.ok) throw new Error(await r.text());
  return r.json();
}

export async function createCampaign(body) {
  const r = await fetch(`https://modularobjects.com/marketing/api/admin/campaigns`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(body),
  });
  if (!r.ok) throw new Error(await r.text());
  return r.json();
}

export async function updateCampaign(id, body) {
  const r = await fetch(`https://modularobjects.com/marketing/api/admin/campaigns/${id}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(body),
  });
  if (!r.ok) throw new Error(await r.text());
  return r.json();
}

export async function fetchCampaignTokens(campaignId) {
  const r = await fetch(`https://modularobjects.com/marketing/api/admin/campaigns/${campaignId}/tokens`, { credentials: "include" });
  if (!r.ok) throw new Error(await r.text());
  return r.json();
}

export async function createCampaignToken(campaignId, body) {
  const r = await fetch(`https://modularobjects.com/marketing/api/admin/campaigns/${campaignId}/tokens`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(body),
  });
  if (!r.ok) throw new Error(await r.text());
  return r.json(); // očekuj { id, code, campaign_id, ... }
}

export async function revokeToken(tokenId) {
  const r = await fetch(`https://modularobjects.com/marketing/api/admin/tokens/${tokenId}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ revoked: 1 }),
  });
  if (!r.ok) throw new Error(await r.text());
  return r.json();
}

export async function fetchRoles() {
  const { data } = await mailboxAxios.get("/crm/settings/roles");
  return data.items || [];
}

export async function createRole(payload) {
  const { data } = await mailboxAxios.post("/crm/settings/roles", payload);
  return data;
}

export async function updateRole(id, payload) {
  const { data } = await mailboxAxios.put(`/crm/settings/roles/${id}`, payload);
  return data;
}

export async function fetchOpportunityDetailsForCrm(id) {
  const res = await fetch(`https://modularobjects.com/marketing/api/crm/opportunities/${id}/details`, {
    credentials: "include",
  });

  let json;
  try {
    json = await res.json();
  } catch {
    const txt = await res.text().catch(() => "");
    throw new Error(txt || `HTTP ${res.status}`);
  }

  if (!res.ok || !json?.success) {
    throw new Error(json?.message || `HTTP ${res.status}`);
  }

  return json.item;
}